import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchPartnerCategory, updatePartnerCategory, fetchPartnerTypes } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function PartnerCategoryEditPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [form, setForm] = useState({
    partnerCategoryName: "",
    partnerCategoryDescription: "",
    selectedPartnerTypeId: "",
  });

  const loadData = useCallback(async () => {
    try {
      const [d, types] = await Promise.all([
        fetchPartnerCategory(categoryId),
        fetchPartnerTypes(businessDomainId),
      ]);
      const typeList = Array.isArray(types) ? types : [];
      setOriginal(d);
      setPartnerTypes(typeList);
      setForm({
        partnerCategoryName: d.partnerCategoryName || "",
        partnerCategoryDescription: d.partnerCategoryDescription || "",
        // partnerTypeId が null の場合も associatedPartnerTypes[0] から補完
        selectedPartnerTypeId:
          d.partnerTypeId ||
          d.associatedPartnerTypes?.[0]?.partnerTypeId ||
          "",
      });
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    try {
      // 選択されたパートナータイプを取得
      const selectedType = partnerTypes.find(
        (pt) => pt.partnerTypeId === form.selectedPartnerTypeId
      );

      // 旧アプリ (EditPartnerCategoryView) と同様に
      // associatedPartnerTypes に選択タイプを設定する
      const associatedPartnerTypes = selectedType
        ? [{ partnerTypeId: selectedType.partnerTypeId, partnerTypeName: selectedType.partnerTypeName }]
        : original.associatedPartnerTypes || [];

      await updatePartnerCategory(categoryId, {
        ...original,
        partnerCategoryName: form.partnerCategoryName,
        partnerCategoryDescription: form.partnerCategoryDescription || null,
        partnerTypeId: form.selectedPartnerTypeId || original.partnerTypeId || null,
        associatedPartnerTypes,
      });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/partner-categories/${categoryId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("partnerCategory.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* パートナータイプ選択 */}
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("partnerCategory.partnerType")}</InputLabel>
              <Select
                value={form.selectedPartnerTypeId}
                onChange={(e) => setForm({ ...form, selectedPartnerTypeId: e.target.value })}
                label={t("partnerCategory.partnerType")}
              >
                {partnerTypes.map((pt) => (
                  <MenuItem key={pt.partnerTypeId} value={pt.partnerTypeId}>
                    {pt.partnerTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* パートナーカテゴリ名 */}
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("partnerCategory.name")}
              name="partnerCategoryName"
              value={form.partnerCategoryName}
              onChange={handleChange}
            />
          </Grid>

          {/* 説明 */}
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("common.description")}
              name="partnerCategoryDescription"
              value={form.partnerCategoryDescription}
              onChange={handleChange}
            />
          </Grid>

        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/partner-categories/${categoryId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
